/* components/ui/toastModel.js — правила тостов без React и без DOM (kitchen-ux A1: K-11, C-49).
   Чистая логика — чтобы её можно было проверить в node и не размазывать по провайдеру.

   · Тон: алиасы (ok/bad/error/warn/neutral/info) сводятся к семантическим токенам.
   · Вежливость live-области: danger — assertive (объявить сразу), прочее — polite.
   · Длительность: по умолчанию 4 с; ошибка — не меньше 8 с (длинные ошибки POS не успевали
     прочитать); 0 — висит, пока не закроют.
   · Таймеры с паузой: наведение и фокус замораживают ВСЕ тосты стека, уход — продолжает
     с остатка, но не меньше секунды (тост не исчезает в момент, когда с него увели мышь).
   · Ссылки: URL выделяется в отдельный фрагмент — на телефоне CSS режет его многоточием.
   · Подвал: стек поднимается над подвалом открытого drawer/модалки, если тот под стеком.
   · Диалог на узком экране (лист снизу): стек над всем листом или под диалогом по слою (E1). */

export const TOAST_DEFAULT_MS = 4000;
export const TOAST_ERROR_MIN_MS = 8000;
const RESUME_MIN_MS = 1000;

// Алиасы устаревших badge-токенов → семантический тон (защита от вызовов toast('ok'/'bad'), P1-14).
// «error» — так зовёт база знаний (DocumentsTab): без алиаса ошибка рисовалась как справка.
export const TONE_ALIAS = { ok: 'success', bad: 'danger', error: 'danger', warn: 'warning', neutral: 'muted', info: 'accent' };

export const normalizeTone = (tone) => TONE_ALIAS[tone] || tone || 'muted';

export const toastPoliteness = (tone) => (normalizeTone(tone) === 'danger' ? 'assertive' : 'polite');

export function toastDuration(tone, requested) {
  const isError = normalizeTone(tone) === 'danger';
  if (requested != null && !(requested > 0)) return 0;          // 0/отрицательное — до закрытия
  const ms = requested == null ? (isError ? TOAST_ERROR_MIN_MS : TOAST_DEFAULT_MS) : requested;
  return isError ? Math.max(ms, TOAST_ERROR_MIN_MS) : ms;
}

/* Таймеры стека. schedule/cancel/now подменяемы (тесты — фейковые часы vitest). */
export function createToastTimers({
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (h) => clearTimeout(h),
  now = () => Date.now(),
} = {}) {
  const items = new Map(); // id → { remaining, onExpire, handle, startedAt }
  let paused = false;

  const arm = (id, t) => {
    t.startedAt = now();
    t.handle = schedule(() => { items.delete(id); t.onExpire(); }, t.remaining);
  };

  return {
    start(id, duration, onExpire) {
      if (!(duration > 0)) return;
      const t = { remaining: duration, onExpire, handle: null, startedAt: 0 };
      items.set(id, t);
      if (!paused) arm(id, t);
    },
    clear(id) {
      const t = items.get(id);
      if (!t) return;
      if (t.handle != null) cancel(t.handle);
      items.delete(id);
    },
    pause() {
      if (paused) return;
      paused = true;
      for (const t of items.values()) {
        if (t.handle == null) continue;
        cancel(t.handle);
        t.handle = null;
        t.remaining = Math.max(0, t.remaining - (now() - t.startedAt));
      }
    },
    resume() {
      if (!paused) return;
      paused = false;
      for (const [id, t] of items) {
        t.remaining = Math.max(t.remaining, RESUME_MIN_MS);
        arm(id, t);
      }
    },
    isPaused: () => paused,
    dispose() {
      for (const t of items.values()) if (t.handle != null) cancel(t.handle);
      items.clear();
    },
  };
}

/* Текст тоста → фрагменты [{text, url}]: ссылка отдельно от слов вокруг. */
const URL_RE = /https?:\/\/[^\s<>"']+/g;
export function splitUrls(text) {
  const s = String(text == null ? '' : text);
  const out = [];
  let last = 0;
  for (const m of s.matchAll(URL_RE)) {
    if (m.index > last) out.push({ text: s.slice(last, m.index), url: false });
    out.push({ text: m[0], url: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push({ text: s.slice(last), url: false });
  return out;
}

/* Насколько поднять стек над подвалами (px от низа вьюпорта; 0 — не нужно).
   footRects — прямоугольники подвалов открытых оверлеев, stackRect — стек по горизонтали.
   Подвал учитывается, только если он видим и лежит ПОД стеком по горизонтали: модалка по
   центру десктопа правый нижний угол не занимает, и стек остаётся на своём месте. */
export function footClearance(footRects, viewportHeight, stackRect, gap = 8) {
  let need = 0;
  for (const r of footRects || []) {
    if (!r || r.bottom <= 0 || r.top >= viewportHeight) continue;
    if (stackRect && (r.right <= stackRect.left || r.left >= stackRect.right)) continue;
    need = Math.max(need, viewportHeight - r.top + gap);
  }
  return need;
}

/* Место стека, пока открыт модальный диалог на УЗКОМ экране (kitchen-ux E1, п. 3): там диалог —
   лист снизу (Modal, листы выбора), и подъём над его подвалом ставил стек на текст диалога.
   → { lift, under }:
   · стек помещается между верхом экрана (отступ `top`) и листом — встаёт над ВСЕМ листом
     (lift — px от низа вьюпорта до низа стека), кнопки и текст диалога свободны;
   · не помещается (лист почти во весь экран) — under: стек уходит под диалог по слою
     (диалог читается и нажимается целиком), провайдер держит таймеры до закрытия диалога.
   dialogRects — прямоугольники открытых диалогов: в счёт только видимые, ненулевые и лежащие
   под стеком по горизонтали; несколько (подтверждение поверх листа) — по самому высокому верху. */
export function dialogPlacement(dialogRects, viewportHeight, stackHeight, stackRect, { gap = 8, top = 8 } = {}) {
  let sheetTop = null;
  for (const r of dialogRects || []) {
    if (!r || r.bottom <= 0 || r.top >= viewportHeight || !(r.bottom > r.top)) continue;
    if (stackRect && (r.right <= stackRect.left || r.left >= stackRect.right)) continue;
    sheetTop = sheetTop == null ? r.top : Math.min(sheetTop, r.top);
  }
  if (sheetTop == null) return { lift: 0, under: false };
  const lift = viewportHeight - sheetTop + gap;
  const stackTop = viewportHeight - lift - (stackHeight || 0);
  return stackTop >= top ? { lift, under: false } : { lift: 0, under: true };
}
